import type { ListMailThreadsInput, MailThreadListItem } from '@fos/shared/contracts/mail';
import { CLIENT_MATCH_LABELS, EMAIL_DRAFT_STATUS_LABELS, EMAIL_DRAFT_STATUS_TONES } from '@fos/shared/labels';
import { Lock, Mail, Users } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { StatusPill } from '../../../ui/StatusPill';
import { ClientLabel } from '../../clients/components/ClientLabel';
import { listTime } from '../../inbox/inbox-format';
import { TOUCH_BUTTON } from '../../mobile';
import { addressShort } from '../mail-format';

const PAGE = 30;

/** Wiersz wątku (DESIGN §4 listy: 44–60 px, linia pod spodem): nadawca i czas, temat, zajawka, stan. */
export function ThreadRow({
  thread,
  onOpen,
  showClient = false,
  showSimulated = false,
  now = new Date(),
}: {
  thread: MailThreadListItem;
  onOpen: (id: string) => void;
  showClient?: boolean;
  /** „Symulacja” przy wierszu — tylko gdy lista miesza skrzynki prawdziwe i w symulacji (inaczej mówi to nagłówek) */
  showSimulated?: boolean;
  now?: Date;
}) {
  const sender = thread.lastDirection === 'out' ? 'Ty' : addressShort(thread.lastFrom);
  return (
    <button
      type="button"
      onClick={() => onOpen(thread.id)}
      className="flex w-full flex-col gap-1 border-b border-line py-3 text-left hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
    >
      <span className="flex min-w-0 items-center gap-2 text-caption text-fg-muted">
        {thread.privacy && (
          // skrzynka osobista: kłódka = widzi tylko właściciel, osoby = udostępniony zespołowi
          <span
            title={thread.privacy === 'private' ? 'Tylko Ty' : 'Udostępniony zespołowi'}
            className="inline-flex shrink-0"
          >
            {thread.privacy === 'private' ? (
              <Lock size={12} strokeWidth={1.75} aria-label="Tylko Ty" role="img" />
            ) : (
              <Users size={12} strokeWidth={1.75} aria-label="Udostępniony zespołowi" role="img" />
            )}
          </span>
        )}
        <span className="min-w-0 truncate">{sender}</span>
        {/* lifting 17.10.2026: klient jako chip w jego barwie, stan wątku jako pigułka */}
        {showClient && thread.client && <ClientLabel client={thread.client} />}
        <span className="flex-1" />
        {thread.unresolved && (
          <StatusPill tone="warning" size="s">
            {CLIENT_MATCH_LABELS[thread.unresolved]}
          </StatusPill>
        )}
        {thread.draftStatus && (
          <StatusPill tone={EMAIL_DRAFT_STATUS_TONES[thread.draftStatus]} size="s">
            {EMAIL_DRAFT_STATUS_LABELS[thread.draftStatus]}
          </StatusPill>
        )}
        {showSimulated && thread.simulated && <Status tone="warning">Symulacja</Status>}
        <span className="shrink-0">{listTime(new Date(thread.lastMessageAt), now)}</span>
      </span>
      <span className="flex min-w-0 items-baseline gap-2">
        <span className="truncate text-body-s font-medium text-fg" title={thread.subject}>
          {thread.subject}
        </span>
        {thread.messageCount > 1 && <span className="shrink-0 text-caption text-fg-muted">{thread.messageCount}</span>}
      </span>
      {thread.snippet && <span className="line-clamp-1 text-body-s text-fg-secondary">{thread.snippet}</span>}
    </button>
  );
}

/** Lista wątków (`mail.listThreads`) ze stanami ładowania, pustym i błędu oraz „Pokaż starsze”. */
export function ThreadList({
  input,
  onOpen,
  showClient = false,
  empty,
  className,
}: {
  input: Omit<ListMailThreadsInput, 'limit' | 'cursor'>;
  onOpen: (id: string) => void;
  showClient?: boolean;
  empty?: { title: string; description?: string; action?: ReactNode };
  className?: string;
}) {
  const [limit, setLimit] = useState(PAGE);
  const query = trpc.mail.listThreads.useQuery({ ...input, limit });
  const now = new Date();
  const items = query.data?.items ?? [];
  const mixed = items.some((t) => t.simulated) && items.some((t) => !t.simulated);
  return (
    <section aria-label="Wątki" className={cn(className)}>
      {query.isPending ? (
        <ListSkeleton rows={4} />
      ) : query.isError ? (
        <ErrorState
          compact
          title="Nie udało się wczytać wiadomości"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      ) : query.data.items.length === 0 ? (
        <EmptyState
          compact
          icon={Mail}
          title={empty?.title ?? 'Brak wiadomości.'}
          description={empty?.description}
          action={empty?.action}
        />
      ) : (
        <>
          <div className="flex flex-col">
            {query.data.items.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                onOpen={onOpen}
                showClient={showClient}
                showSimulated={mixed}
                now={now}
              />
            ))}
          </div>
          {query.data.nextCursor && limit < 100 && (
            <div className="pt-2">
              <Button
                variant="ghost"
                size="s"
                className={TOUCH_BUTTON}
                loading={query.isFetching}
                onClick={() => setLimit((l) => Math.min(l + PAGE, 100))}
              >
                Pokaż starsze
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
